import { useEffect, useMemo, useRef, useState } from 'react';
import type { CircleMarker as LeafletCircleMarker, Map as LeafletMap } from 'leaflet';
import { CircleMarker, MapContainer, Popup, TileLayer, useMap } from 'react-leaflet';
import { AlertCircle, Crosshair, Layers3, List, MapPin, Navigation } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, currency, googleMapsDirectionsUrl } from '../api';
import { ErrorState, Loading } from '../components/Loading';
import type { Offer } from '../types';

const defaultCenter: [number, number] = [52.2297, 21.0122];
type MapConfig = { tileUrl: string; attribution: string };

function MapViewport({ offer }: { offer: Offer | null }) {
  const map = useMap();
  useEffect(() => {
    if (offer?.latitude == null || offer.longitude == null) return;
    const zoom = offer.locationPrecision === 'exact' ? 18 : offer.locationPrecision === 'street' ? 16 : 13;
    map.setView([offer.latitude, offer.longitude], zoom, { animate: true });
  }, [map, offer]);
  return null;
}

function OfferMarker({ offer, selected, onOpen }: { offer: Offer; selected: boolean; onOpen: () => void }) {
  const marker = useRef<LeafletCircleMarker | null>(null);
  useEffect(() => {
    if (!selected) return;
    const timer = setTimeout(() => marker.current?.openPopup(), 120);
    return () => clearTimeout(timer);
  }, [selected]);
  const exact = offer.locationPrecision === 'exact';
  const directionsUrl = googleMapsDirectionsUrl({ latitude: offer.latitude, longitude: offer.longitude, address: offer.address, city: offer.city });
  return <CircleMarker ref={marker} center={[offer.latitude!, offer.longitude!]} radius={selected ? 17 : 13} pathOptions={{ color: '#fff', weight: 4, fillColor: exact ? '#0b56ff' : '#f59e0b', fillOpacity: 1 }}>
    <Popup><div className="map-popup-card"><button className="map-popup" onClick={onOpen}><img src={offer.coverUrl || '/images/offer-placeholder.svg'} alt="" /><strong>{offer.title}</strong><span>{currency(offer.price)}</span><small>{offer.address || offer.city}</small><em className={exact ? 'location-exact' : 'location-approximate'}><MapPin />{exact ? 'Dokładny adres' : 'Lokalizacja przybliżona'}</em></button>{directionsUrl && <a href={directionsUrl} target="_blank" rel="noreferrer"><Navigation />Nawiguj w Google Maps</a>}</div></Popup>
  </CircleMarker>;
}

export default function MapPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedId = searchParams.get('offer');
  const [offers, setOffers] = useState<Offer[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState(false);
  const [map, setMap] = useState<LeafletMap | null>(null);
  const [config, setConfig] = useState<MapConfig | null>(null);
  useEffect(() => {
    Promise.all([api<{ items: Offer[] }>('/api/offers'), api<MapConfig>('/api/map-config')]).then(([data, mapConfig]) => { setOffers(data.items); setConfig(mapConfig); setError(''); }).catch(err => setError(err.message)).finally(() => setLoading(false));
  }, []);
  const located = useMemo(() => offers.filter(offer => offer.latitude != null && offer.longitude != null), [offers]);
  const missing = offers.length - located.length;
  const selected = located.find(offer => offer.id === selectedId) || null;
  const center = useMemo<[number, number]>(() => selected ? [selected.latitude!, selected.longitude!] : located[0] ? [located[0].latitude!, located[0].longitude!] : defaultCenter, [located, selected]);
  const locateUser = () => navigator.geolocation?.getCurrentPosition(position => map?.setView([position.coords.latitude, position.coords.longitude], 14, { animate: true }));
  if (loading) return <Loading label="Przygotowuję prywatną mapę…" />;
  if (error) return <ErrorState message={error} />;
  if (!config) return <ErrorState message="Nie udało się pobrać konfiguracji mapy." />;
  return <div className="map-page"><header><div><h1>Mapa</h1><p>{located.length} zlokalizowanych ofert{missing ? ` · ${missing} bez współrzędnych` : ''}</p></div><button onClick={() => setList(value => !value)} aria-label={list ? 'Pokaż mapę' : 'Pokaż listę'}>{list ? <Layers3 /> : <List />}</button></header>
    {!list ? <div className="map-wrap">
      <MapContainer ref={setMap} center={center} zoom={selected ? (selected.locationPrecision === 'exact' ? 18 : 13) : 8} scrollWheelZoom className="leaflet-map">
        <TileLayer attribution={config.attribution} url={config.tileUrl} />
        <MapViewport offer={selected} />
        {located.map(offer => <OfferMarker key={offer.id} offer={offer} selected={offer.id === selectedId} onOpen={() => navigate(`/oferty/${offer.id}`)} />)}
      </MapContainer>
      <div className="map-legend"><span><i className="exact" />Dokładny adres</span><span><i className="approximate" />Miejscowość / okolica</span></div>
      {!located.length && <div className="map-empty"><AlertCircle /><strong>Brak ofert z ustaloną lokalizacją</strong><p>Uzupełnij miejscowość lub ulicę i numer na karcie oferty.</p></div>}
      <button className="locate-button" onClick={locateUser} aria-label="Pokaż moją lokalizację"><Crosshair /></button>
    </div> : <div className="map-list">{offers.map(offer => <button key={offer.id} onClick={() => offer.latitude != null ? navigate(`/mapa?offer=${offer.id}`) : navigate(`/oferty/${offer.id}`)}><img src={offer.coverUrl || '/images/offer-placeholder.svg'} alt="" /><p><strong>{offer.title}</strong><span>{currency(offer.price)}</span><small>{offer.address || offer.city || 'Brak adresu'}</small>{offer.latitude == null && <em>Uzupełnij adres, aby dodać pinezkę</em>}</p></button>)}</div>}
  </div>;
}
